import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    LinearProgress,
    List,
    ListItemButton,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import StarIcon from '@mui/icons-material/Star';
import { MusicAlbumRow, MusicPlaylist, MusicTrack, MusicTrackUpdate, formatBytes, formatDuration, musicService, triggerDownload } from '../../api/music';
import { AlbumCards } from './AlbumCards';
import { AlbumTree, NO_ALBUM, buildAlbumTree } from './AlbumTree';
import { TrackEditDialog } from './TrackEditDialog';
import { StarRating } from './StarRating';
import { AdvancedSearch } from './AdvancedSearch';
import { PlayerControls } from './PlayerControls';
import { useMusicPlayer } from './MusicPlayerContext';

const DOWNLOAD_CONFIRM_BYTES = 500 * 1024 * 1024;
const DOWNLOAD_DELAY_MS = 700;
const BEST_MIN_RATING = 4;

const ACCEPT = '.mp3,.wav,.ogg,.oga,.opus,.flac,.m4a,.aac,.webm,audio/*';

export const MusicPage = () => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const player = useMusicPlayer();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [search, setSearch] = useState('');
    const [selectedPlaylistId, setSelectedPlaylistId] = useState<string | null>(null);
    const [upload, setUpload] = useState<{ done: number; total: number; fraction: number } | null>(null);
    const [menu, setMenu] = useState<{ anchor: HTMLElement; track: MusicTrack } | null>(null);
    const [newPlaylistName, setNewPlaylistName] = useState('');
    const [selectedAlbum, setSelectedAlbum] = useState<string | null>(null);
    const [editTrack, setEditTrack] = useState<MusicTrack | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [viewMode, setViewMode] = useState<'tree' | 'cards'>(() => (window.localStorage.getItem('music.viewMode') === 'cards' ? 'cards' : 'tree'));
    const [ratingSort, setRatingSort] = useState<'asc' | 'desc' | null>(null);

    const { setRemoteListening, syncTracks } = player;
    useEffect(() => {
        setRemoteListening(true);
        return () => setRemoteListening(false);
    }, [setRemoteListening]);

    const tracksQuery = useQuery<MusicTrack[]>({ queryKey: ['music-tracks'], queryFn: () => musicService.tracks() });
    const albumsQuery = useQuery<MusicAlbumRow[]>({ queryKey: ['music-albums'], queryFn: () => musicService.albums() });
    const albumCardsQuery = useQuery<MusicAlbumRow[]>({ queryKey: ['music-albums', 'cards'], queryFn: () => musicService.albums(true), enabled: viewMode === 'cards' });
    const albumTracksQuery = useQuery<MusicTrack[]>({
        queryKey: ['music-tracks', 'album', selectedAlbum],
        queryFn: () => musicService.tracks('', selectedAlbum as string),
        enabled: selectedAlbum !== null && selectedAlbum !== NO_ALBUM && !selectedPlaylistId,
    });
    const playlistsQuery = useQuery<MusicPlaylist[]>({ queryKey: ['music-playlists'], queryFn: musicService.playlists });
    const playlistQuery = useQuery<MusicPlaylist>({
        queryKey: ['music-playlist', selectedPlaylistId],
        queryFn: () => musicService.playlist(selectedPlaylistId as string),
        enabled: Boolean(selectedPlaylistId),
    });

    useEffect(() => {
        if (tracksQuery.data) syncTracks(tracksQuery.data);
    }, [tracksQuery.data, syncTracks]);

    const visibleTracks = useMemo(() => {
        let base: MusicTrack[];
        if (selectedPlaylistId) {
            base = playlistQuery.data?.tracks ?? [];
        } else if (selectedAlbum === NO_ALBUM) {
            base = (tracksQuery.data ?? []).filter((track) => !track.album_path);
        } else if (selectedAlbum !== null) {
            base = albumTracksQuery.data ?? [];
        } else {
            base = tracksQuery.data ?? [];
        }
        const term = search.trim().toLowerCase();
        const filtered = !term ? base : base.filter((track) => [track.title, track.artist, track.album, track.original_name, track.source, track.license]
            .filter(Boolean).join(' ').toLowerCase().includes(term));
        if (!ratingSort) return filtered;
        const direction = ratingSort === 'asc' ? 1 : -1;
        // stable: tracks with the same rating keep the library order
        return [...filtered].sort((a, b) => ((a.rating ?? 0) - (b.rating ?? 0)) * direction);
    }, [albumTracksQuery.data, playlistQuery.data, ratingSort, search, selectedAlbum, selectedPlaylistId, tracksQuery.data]);

    const refreshAll = async () => {
        await queryClient.invalidateQueries({ queryKey: ['music-tracks'] });
        await queryClient.invalidateQueries({ queryKey: ['music-albums'] });
        await queryClient.invalidateQueries({ queryKey: ['music-playlists'] });
        await queryClient.invalidateQueries({ queryKey: ['music-playlist'] });
    };

    const handleFiles = async (files: FileList | null) => {
        if (!files || files.length === 0) return;
        const list = Array.from(files);
        setUpload({ done: 0, total: list.length, fraction: 0 });
        const uploaded: MusicTrack[] = [];
        for (const [i, file] of list.entries()) {
            try {
                const track = await musicService.upload(file, (fraction) => setUpload({ done: i, total: list.length, fraction }));
                uploaded.push(track);
            } catch {
                enqueueSnackbar(t('music.uploadError', { name: file.name }), { variant: 'error' });
            }
            setUpload({ done: i + 1, total: list.length, fraction: 0 });
        }
        if (selectedPlaylistId && uploaded.length > 0 && playlistQuery.data) {
            await musicService.updatePlaylist(selectedPlaylistId, {
                track_ids: [...(playlistQuery.data.track_ids ?? []), ...uploaded.map((track) => track.id)],
            });
        }
        setUpload(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        await refreshAll();
    };

    const deleteTrack = useMutation({
        mutationFn: (id: string) => musicService.deleteTrack(id),
        onSuccess: refreshAll,
    });

    const updateTrack = useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: MusicTrackUpdate }) => musicService.updateTrack(id, payload),
        onSuccess: async () => {
            setEditTrack(null);
            await refreshAll();
        },
        onError: () => enqueueSnackbar(t('music.saveError'), { variant: 'error' }),
    });

    const rateTrack = useMutation({
        mutationFn: ({ id, rating }: { id: string; rating: number }) => musicService.updateTrack(id, { rating }),
        onSuccess: refreshAll,
        onError: () => enqueueSnackbar(t('music.saveError'), { variant: 'error' }),
    });

    const downloadTrack = async (track: MusicTrack) => {
        try {
            const { url, filename } = await musicService.downloadUrl(track.id);
            triggerDownload(url, filename);
        } catch {
            enqueueSnackbar(t('music.downloadError'), { variant: 'error' });
        }
    };

    const albumTree = useMemo(() => buildAlbumTree(albumsQuery.data ?? []), [albumsQuery.data]);
    const selectedNode = selectedAlbum && selectedAlbum !== NO_ALBUM ? albumTree.nodes.get(selectedAlbum) ?? null : null;

    const bestCount = useMemo(
        () => (albumTracksQuery.data ?? []).filter((track) => (track.rating ?? 0) >= BEST_MIN_RATING).length,
        [albumTracksQuery.data],
    );

    /** Sequential browser downloads of an album node (optionally only tracks rated at least `minRating`). */
    const downloadAlbum = async (minRating = 0) => {
        if (!selectedNode || downloading) return;
        setDownloading(true);
        try {
            const manifest = await musicService.manifest(selectedNode.path, minRating);
            if (manifest.tracks.length === 0) {
                enqueueSnackbar(t('music.downloadEmpty'), { variant: 'info' });
                return;
            }
            const size = formatBytes(manifest.total_size_bytes);
            if (manifest.total_size_bytes > DOWNLOAD_CONFIRM_BYTES
                && !window.confirm(t('music.downloadConfirm', { count: manifest.tracks.length, size }))) {
                return;
            }
            enqueueSnackbar(t('music.downloadStarted', { count: manifest.tracks.length, size }), { variant: 'info' });
            for (const item of manifest.tracks) {
                triggerDownload(item.download_url, item.filename);
                await new Promise((resolve) => { window.setTimeout(resolve, DOWNLOAD_DELAY_MS); });
            }
        } catch {
            enqueueSnackbar(t('music.downloadError'), { variant: 'error' });
        } finally {
            setDownloading(false);
        }
    };

    const createPlaylist = useMutation({
        mutationFn: (name: string) => musicService.createPlaylist(name),
        onSuccess: async (playlist) => {
            setNewPlaylistName('');
            setSelectedPlaylistId(playlist.id);
            await refreshAll();
        },
    });

    const deletePlaylist = useMutation({
        mutationFn: (id: string) => musicService.deletePlaylist(id),
        onSuccess: async () => {
            setSelectedPlaylistId(null);
            await refreshAll();
        },
    });

    const addToPlaylist = async (playlist: MusicPlaylist, track: MusicTrack) => {
        const full = await musicService.playlist(playlist.id);
        const ids = full.track_ids ?? [];
        if (!ids.includes(track.id)) {
            await musicService.updatePlaylist(playlist.id, { track_ids: [...ids, track.id] });
        }
        await refreshAll();
    };

    const removeFromPlaylist = async (track: MusicTrack) => {
        if (!selectedPlaylistId || !playlistQuery.data) return;
        await musicService.updatePlaylist(selectedPlaylistId, {
            track_ids: (playlistQuery.data.track_ids ?? []).filter((id) => id !== track.id),
        });
        await refreshAll();
    };

    const playFrom = (trackIndex: number) => player.playQueue(visibleTracks, trackIndex, selectedPlaylistId);

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4">{t('music.title')}</Typography>
                <Button variant="contained" startIcon={<UploadIcon />} onClick={() => fileInputRef.current?.click()} disabled={upload !== null}>
                    {t('music.upload')}
                </Button>
                <input ref={fileInputRef} type="file" accept={ACCEPT} multiple hidden onChange={(event) => void handleFiles(event.target.files)} />
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <PlayerControls />
                {player.lastRemote && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {t('music.remoteBy', { name: player.lastRemote.by ?? '?' })}: {player.lastRemote.action}
                    </Typography>
                )}
            </Paper>

            {upload && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2">{t('music.uploading', { done: upload.done, total: upload.total })}</Typography>
                    <LinearProgress variant="determinate" value={((upload.done + upload.fraction) / upload.total) * 100} />
                </Box>
            )}

            <AdvancedSearch
                albums={albumsQuery.data ?? []}
                tracks={tracksQuery.data ?? []}
                onRate={(track, rating) => rateTrack.mutate({ id: track.id, rating })}
                onDownload={(track) => void downloadTrack(track)}
            />

            <ToggleButtonGroup
                size="small"
                exclusive
                value={viewMode}
                onChange={(_event, next: 'tree' | 'cards' | null) => {
                    if (!next) return;
                    setViewMode(next);
                    window.localStorage.setItem('music.viewMode', next);
                }}
                sx={{ mb: 2 }}
            >
                <ToggleButton value="tree"><AccountTreeIcon fontSize="small" sx={{ mr: 0.5 }} />{t('music.view.tree')}</ToggleButton>
                <ToggleButton value="cards"><ViewModuleIcon fontSize="small" sx={{ mr: 0.5 }} />{t('music.view.cards')}</ToggleButton>
            </ToggleButtonGroup>

            {viewMode === 'cards' && (
                <AlbumCards
                    rows={albumCardsQuery.data ?? []}
                    selected={selectedAlbum}
                    onSelect={(value) => {
                        setSelectedPlaylistId(null);
                        setSelectedAlbum(value);
                    }}
                />
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Paper variant="outlined" sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0 }}>
                    {viewMode === 'tree' && (
                        <>
                            <Typography variant="overline" sx={{ px: 2, display: 'block', mt: 1 }}>{t('music.albums')}</Typography>
                            <AlbumTree
                                rows={albumsQuery.data ?? []}
                                selected={selectedPlaylistId ? '\u0001' : selectedAlbum}
                                onSelect={(value) => {
                                    setSelectedPlaylistId(null);
                                    setSelectedAlbum(value);
                                }}
                            />
                        </>
                    )}
                    <List dense>
                        <Typography variant="overline" sx={{ px: 2, display: 'block' }}>{t('music.playlists')}</Typography>
                        {(playlistsQuery.data ?? []).map((playlist) => (
                            <ListItemButton
                                key={playlist.id}
                                selected={selectedPlaylistId === playlist.id}
                                onClick={() => setSelectedPlaylistId(playlist.id)}
                            >
                                <ListItemText primary={playlist.name} secondary={`${playlist.tracks_count}`} />
                            </ListItemButton>
                        ))}
                    </List>
                    <Stack direction="row" spacing={1} sx={{ p: 1.5, pt: 0 }}>
                        <TextField
                            size="small"
                            placeholder={t('music.playlistName')}
                            value={newPlaylistName}
                            onChange={(event) => setNewPlaylistName(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && newPlaylistName.trim()) createPlaylist.mutate(newPlaylistName.trim());
                            }}
                        />
                        <Tooltip title={t('music.newPlaylist')}>
                            <span>
                                <IconButton
                                    color="primary"
                                    disabled={!newPlaylistName.trim() || createPlaylist.isPending}
                                    onClick={() => createPlaylist.mutate(newPlaylistName.trim())}
                                >
                                    <AddIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Paper>

                {!(viewMode === 'cards' && selectedAlbum === null && !selectedPlaylistId) && (
                <Paper variant="outlined" sx={{ flex: 1, p: 2, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder={t('music.search')}
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        />
                        {!selectedPlaylistId && selectedNode && (
                            <Button
                                startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                                variant="outlined"
                                onClick={() => void downloadAlbum()}
                                disabled={downloading}
                                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                                {t('music.downloadAlbum')}
                            </Button>
                        )}
                        {!selectedPlaylistId && selectedNode && (
                            <Tooltip title={t('music.downloadBestHint', { min: BEST_MIN_RATING })}>
                                <span>
                                    <Button
                                        startIcon={downloading ? <CircularProgress size={16} /> : <StarIcon />}
                                        variant="outlined"
                                        onClick={() => void downloadAlbum(BEST_MIN_RATING)}
                                        disabled={downloading || (albumTracksQuery.isSuccess && bestCount === 0)}
                                        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                    >
                                        {t('music.downloadBest')}{albumTracksQuery.isSuccess ? ` (${bestCount})` : ''}
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        {visibleTracks.length > 0 && (
                            <Button startIcon={<PlayArrowIcon />} variant="outlined" onClick={() => playFrom(0)}>{t('music.play')}</Button>
                        )}
                        {selectedPlaylistId && (
                            <Button
                                color="error"
                                onClick={() => deletePlaylist.mutate(selectedPlaylistId)}
                                disabled={deletePlaylist.isPending}
                            >
                                {t('music.deletePlaylist')}
                            </Button>
                        )}
                    </Stack>

                    {selectedNode && !selectedPlaylistId && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {selectedNode.path.split('/').join(' / ')} · {t('music.selectedTracks', { count: selectedNode.totalCount, size: formatBytes(selectedNode.totalSize) })}
                        </Typography>
                    )}
                    {(tracksQuery.isLoading || playlistQuery.isFetching || albumTracksQuery.isFetching) && <CircularProgress size={22} />}
                    {tracksQuery.isError && <Alert severity="error">{t('common.errorFetchingData')}</Alert>}
                    {!tracksQuery.isLoading && visibleTracks.length === 0 && (
                        <Typography color="text.secondary" sx={{ py: 2 }}>{t('music.empty')}</Typography>
                    )}

                    {visibleTracks.length > 0 && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell width={48} />
                                    <TableCell>{t('music.trackTitle')}</TableCell>
                                    <TableCell>{t('music.artist')}</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('music.album')}</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{t('music.source')}</TableCell>
                                    <TableCell sortDirection={ratingSort ?? false} sx={{ whiteSpace: 'nowrap' }}>
                                        <TableSortLabel
                                            active={ratingSort !== null}
                                            direction={ratingSort ?? 'desc'}
                                            onClick={() => setRatingSort((prev) => (prev === null ? 'desc' : prev === 'desc' ? 'asc' : null))}
                                        >
                                            {t('music.rating')}
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">{t('music.duration')}</TableCell>
                                    <TableCell width={160} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleTracks.map((track, trackIndex) => {
                                    const isCurrent = player.current?.id === track.id;
                                    return (
                                        <TableRow key={track.id} hover selected={isCurrent} onDoubleClick={() => playFrom(trackIndex)}>
                                            <TableCell>
                                                <IconButton size="small" onClick={() => (isCurrent ? player.toggle() : playFrom(trackIndex))}>
                                                    {isCurrent && player.isPlaying ? <GraphicEqIcon color="primary" /> : <PlayArrowIcon />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: isCurrent ? 600 : undefined }}>{track.title}</TableCell>
                                            <TableCell>{track.artist || '—'}</TableCell>
                                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{track.album || '—'}</TableCell>
                                            <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                    {track.source && <Chip size="small" label={track.source} />}
                                                    {track.license && <Chip size="small" variant="outlined" label={track.license} title={t('music.license')} />}
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <StarRating value={track.rating} onChange={(rating) => rateTrack.mutate({ id: track.id, rating })} />
                                            </TableCell>
                                            <TableCell align="right">{formatDuration(track.duration_seconds)}</TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                {selectedPlaylistId ? (
                                                    <Tooltip title={t('music.removeFromPlaylist')}>
                                                        <IconButton size="small" onClick={() => void removeFromPlaylist(track)}>
                                                            <PlaylistRemoveIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title={t('music.addToPlaylist')}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={(playlistsQuery.data ?? []).length === 0}
                                                                onClick={(event) => setMenu({ anchor: event.currentTarget, track })}
                                                            >
                                                                <PlaylistAddIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title={t('music.download')}>
                                                    <IconButton size="small" onClick={() => void downloadTrack(track)}>
                                                        <DownloadIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('music.editTrack')}>
                                                    <IconButton size="small" onClick={() => setEditTrack(track)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('music.deleteTrack')}>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => window.confirm(t('music.deleteTrackConfirm')) && deleteTrack.mutate(track.id)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>{t('music.remote')}</Typography>
                </Paper>
                )}
            </Stack>

            <TrackEditDialog
                track={editTrack}
                saving={updateTrack.isPending}
                onClose={() => setEditTrack(null)}
                onSave={(id, payload) => updateTrack.mutate({ id, payload })}
            />

            <Menu open={menu !== null} anchorEl={menu?.anchor} onClose={() => setMenu(null)}>
                {(playlistsQuery.data ?? []).map((playlist) => (
                    <MenuItem
                        key={playlist.id}
                        onClick={() => {
                            if (menu) void addToPlaylist(playlist, menu.track);
                            setMenu(null);
                        }}
                    >
                        {playlist.name}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};
